/**
 * CardWidget Component
 *
 * A reusable component for card-based widgets like Trending Deals and Recently Viewed.
 * Provides consistent structure, styling, and behavior across all card widgets.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { CardWidgetProps } from '../../types/CardWidget';
import './CardWidget.css';

const CardWidget: React.FC<CardWidgetProps> = ({
  title,
  icon,
  className = '',
  actionLink,
  headerActions,
  children,
  loading,
  error,
  empty,
  footer,
  headerDivider = true,
  ariaLabel,
  testId,
}) => {
  const widgetClasses = `card-widget ${className}`.trim();

  // Loading state
  if (loading?.isLoading) {
    return (
      <div
        className={widgetClasses}
        aria-label={ariaLabel || `${title} widget`}
        data-testid={testId}
      >
        <div className="card-widget-header">
          <h3>
            {icon && <span className="widget-icon">{icon}</span>}
            {title}
          </h3>
        </div>
        {headerDivider && <div className="header-divider" />}
        <div className="card-widget-body">
          <div className="widget-loading">
            {loading.spinner && <div className="widget-spinner" />}
            {loading.message && <p className="loading-message">{loading.message}</p>}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={widgetClasses}
        aria-label={ariaLabel || `${title} widget`}
        data-testid={testId}
      >
        <div className="card-widget-header">
          <h3>
            {icon && <span className="widget-icon">{icon}</span>}
            {title}
          </h3>
        </div>
        {headerDivider && <div className="header-divider" />}
        <div className="card-widget-body">
          <div className="widget-error">
            <p className="error-message">{error.message}</p>
            {error.retry && (
              <button className="retry-button" onClick={error.retry}>
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (empty && React.Children.count(children) === 0) {
    return (
      <div
        className={widgetClasses}
        aria-label={ariaLabel || `${title} widget`}
        data-testid={testId}
      >
        <div className="card-widget-header">
          <h3>
            {icon && <span className="widget-icon">{icon}</span>}
            {title}
          </h3>
        </div>
        {headerDivider && <div className="header-divider" />}
        <div className="card-widget-body">
          <div className="widget-empty">
            {empty.icon && <span className="empty-icon">{empty.icon}</span>}
            <p className="empty-message">{empty.message}</p>
            {empty.hint && <span className="empty-hint">{empty.hint}</span>}
            {empty.action && (
              <button className="empty-action" onClick={empty.action.onClick}>
                {empty.action.text}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Normal state with content
  return (
    <div className={widgetClasses} aria-label={ariaLabel || `${title} widget`} data-testid={testId}>
      <div className="card-widget-header">
        <h3>
          {icon && <span className="widget-icon">{icon}</span>}
          {title}
        </h3>
        <div className="header-actions">
          {actionLink &&
            (actionLink.onClick ? (
              <a href={actionLink.href} className="widget-action-link" onClick={actionLink.onClick}>
                {actionLink.text}
              </a>
            ) : (
              <Link to={actionLink.href} className="widget-action-link">
                {actionLink.text}
              </Link>
            ))}
          {headerActions}
        </div>
      </div>
      {headerDivider && <div className="header-divider" />}
      <div className="card-widget-body">{children}</div>
      {footer && <div className="card-widget-footer">{footer}</div>}
    </div>
  );
};

export default CardWidget;
